import React, { useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, TextField,
    Typography, Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { OhipTaskService } from 'crqlar-react-common/dist/services/integrations/ohip/ohipTask.service';
import { LoadingButton } from '@mui/lab';

interface Props {
    open: boolean;
    onClose: () => void;
    configId: string;
    onSuccess?: () => void;
}

const FetchEntitiesDialog = ({ open, onClose, configId, onSuccess }: Props) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'profiles' | 'reservations'>('profiles');
    const [ids, setIds] = useState<string>('');

    const fetchMutation = useMutation(
        'fetchEntities',
        async () => {
            if (activeTab === 'profiles') {
                return await OhipTaskService.fetchProfiles(configId, ids.split('\n').filter(id => id.trim() !== ''));
            } else {
                return await OhipTaskService.fetchReservations(configId, ids.split('\n').filter(id => id.trim() !== ''));
            }
        },
        {
            onSuccess: () => {
                onSuccess?.();
                onClose();
                setIds('');
            }
        }
    );

    const handleSubmit = () => {
        fetchMutation.mutate();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('Fetch specific entities')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue) => setActiveTab(newValue)}
                        aria-label="entity type tabs"
                    >
                        <Tab label={t('Profiles')} value="profiles" />
                        <Tab label={t('Reservations')} value="reservations" />
                    </Tabs>

                    <Alert severity="info">
                        {activeTab === 'profiles'
                            ? t('Enter OHIP profile IDs to fetch them from the OHIP system. This will create a sync task that fetches and stores these profiles in the system.')
                            : t('Enter OHIP reservation IDs to fetch them from the OHIP system. This will create a sync task that fetches and stores these reservations in the system.')
                        }
                    </Alert>

                    <TextField
                        multiline
                        rows={6}
                        value={ids}
                        onChange={(e) => setIds(e.target.value)}
                        placeholder={t('Enter IDs, one per line')}
                        variant="outlined"
                        fullWidth
                    />

                    {fetchMutation.isError && (
                        <Typography color="error">
                            {t('An error occurred while fetching entities')}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    loading={fetchMutation.isLoading}
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!ids.trim()}
                >
                    {t('Fetch')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default FetchEntitiesDialog;